import { z } from 'zod';

import { PageQuerySchema, SearchQuerySchema } from '../shared/pagination';

// "<connectionId>.<toolName>" — the namespaced id a materialized MCP handler carries,
// and the only shape an agent's enabledTools may hold for an MCP tool. The shape is
// constrained to what the Gemini API accepts as a function-declaration name: must
// start with a letter, chars in [a-z0-9_.-], ≤63 chars total.
export const McpToolRefSchema = z
  .string()
  .max(63)
  .regex(/^[a-z][a-z0-9-]*\.[a-z0-9_-]+$/)
  .brand<'McpToolRef'>();
export type McpToolRef = z.infer<typeof McpToolRefSchema>;

// Starts with a letter so every derived "<id>.<tool>" ref is a valid Gemini
// function name.
const connectionId = z
  .string()
  .regex(/^[a-z][a-z0-9-]*$/)
  .max(40)
  .refine((id) => id !== 'agent', 'connection id "agent" is reserved for peer-agent tool refs');

// The most calls the platform may have in flight to this server at once. null means
// no limit — the rate limit being respected belongs to the remote server, and most
// servers do not need one. Bounded by smallint.
const maxConcurrentCalls = z.number().int().min(1).max(1000).nullable().default(null);

// A connection to a remote MCP server the agents may consume, over Streamable HTTP.
// The bearer token is write-only: entered in the admin UI, encrypted at rest, and
// never returned by a read — only `bearerTokenSet` says whether one is configured.
export const McpConnectionSchema = z.object({
  id: connectionId,
  name: z.string().min(1).max(120),
  url: z.url(),
  bearerTokenSet: z.boolean(),
  enabled: z.boolean().default(true),
  maxConcurrentCalls,
});
export type McpConnection = z.infer<typeof McpConnectionSchema>;

// One allowlisted tool on a connection. The allowlist is the fail-closed contract for
// which remote tools an agent may ever see. `write` entries materialize too, so a remote
// write is gated by two independent operator acts — an enabled allowlist entry here, and
// the ref appearing in an agent version's enabledTools — and every call is audit-logged.
export const McpToolAllowlistEntrySchema = z.object({
  connectionId,
  toolName: z.string().min(1).max(120),
  toolClass: z.enum(['read', 'write']),
  enabled: z.boolean().default(true),
});
export type McpToolAllowlistEntry = z.infer<typeof McpToolAllowlistEntrySchema>;

// --- MCP connections: write + read envelopes ---
// Write shape: bearerToken is write-only and optional — omitted on create leaves it
// unset; omitted on update leaves the stored ciphertext unchanged. A blank UI field
// submits as undefined.
const McpConnectionWriteShape = {
  id: connectionId,
  name: z.string().min(1).max(120),
  url: z.url(),
  bearerToken: z.string().min(1).optional(),
  enabled: z.boolean().default(true),
  maxConcurrentCalls,
};
export const CreateMcpConnectionSchema = z.object(McpConnectionWriteShape); // id supplied by the operator (slug)
export type CreateMcpConnection = z.infer<typeof CreateMcpConnectionSchema>;

// id is immutable (it's the ref prefix); everything else is editable.
export const UpdateMcpConnectionSchema = z.object(McpConnectionWriteShape).omit({ id: true });
export type UpdateMcpConnection = z.infer<typeof UpdateMcpConnectionSchema>;

// Live runtime status from McpConnectionManager + ToolRegistry (not the DB).
export const McpConnectionStatusSchema = z.object({
  connected: z.boolean(),
  liveToolCount: z.number().int().nonnegative(),
});
export type McpConnectionStatus = z.infer<typeof McpConnectionStatusSchema>;

export const McpConnectionSummarySchema = McpConnectionSchema.extend({
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  allowlistCount: z.number().int().nonnegative(),
  status: McpConnectionStatusSchema,
});
export type McpConnectionSummary = z.infer<typeof McpConnectionSummarySchema>;

export const McpConnectionPageSchema = z.object({
  items: z.array(McpConnectionSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type McpConnectionPage = z.infer<typeof McpConnectionPageSchema>;

export const McpAllowlistEntrySchema = McpToolAllowlistEntrySchema.extend({
  id: z.uuid(),
});
export type McpAllowlistEntry = z.infer<typeof McpAllowlistEntrySchema>;

export const McpConnectionDetailSchema = McpConnectionSummarySchema.extend({
  allowlist: z.array(McpAllowlistEntrySchema),
});
export type McpConnectionDetail = z.infer<typeof McpConnectionDetailSchema>;

export const ListMcpConnectionsQuerySchema = PageQuerySchema.merge(SearchQuerySchema);
export type ListMcpConnectionsQuery = z.infer<typeof ListMcpConnectionsQuerySchema>;

// --- Allowlist writes ---
export const CreateMcpAllowlistEntrySchema = McpToolAllowlistEntrySchema;
export type CreateMcpAllowlistEntry = z.infer<typeof CreateMcpAllowlistEntrySchema>;
export const UpdateMcpAllowlistEntrySchema = McpToolAllowlistEntrySchema.pick({
  toolClass: true,
  enabled: true,
});
export type UpdateMcpAllowlistEntry = z.infer<typeof UpdateMcpAllowlistEntrySchema>;

// --- Discovery ---
export const DiscoveredToolSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  readOnlyHint: z.boolean().optional(),
});
export type DiscoveredTool = z.infer<typeof DiscoveredToolSchema>;
export const DiscoverResultSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), tools: z.array(DiscoveredToolSchema) }),
  z.object({ ok: z.literal(false), reason: z.string() }),
]);
export type DiscoverResult = z.infer<typeof DiscoverResultSchema>;
